import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Mark } from '@/components/layout/Mark';
import { toast } from 'sonner';
import { Shield, ArrowLeft } from 'lucide-react';

const loginSchema = z.object({
  email: z.string().email('E-mail inválido'),
  password: z.string().min(6, 'Senha precisa ter no mínimo 6 caracteres'),
});

type LoginInput = z.infer<typeof loginSchema>;

export const Route = createFileRoute('/login')({
  component: LoginPage,
});

function LoginPage() {
  const { login, loginMfa } = useAuth();
  const navigate = useNavigate();
  const [mfaChallenge, setMfaChallenge] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaSubmitting, setMfaSubmitting] = useState(false);

  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  async function onSubmit(data: LoginInput) {
    try {
      const r = await login(data.email, data.password);
      if (r.ok) {
        toast.success('Bem-vindo de volta!');
        navigate({ to: '/' });
      } else {
        setMfaChallenge(r.mfaChallenge);
      }
    } catch {
      toast.error('E-mail ou senha incorretos.');
    }
  }

  async function onSubmitMfa(e: React.FormEvent) {
    e.preventDefault();
    if (!mfaChallenge || mfaCode.length !== 6) return;
    setMfaSubmitting(true);
    try {
      await loginMfa(mfaChallenge, mfaCode);
      toast.success('Bem-vindo de volta!');
      navigate({ to: '/' });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Código incorreto.');
    } finally {
      setMfaSubmitting(false);
    }
  }

  function backToPassword() {
    setMfaChallenge(null);
    setMfaCode('');
  }

  return (
    <div className="min-h-dvh flex items-center justify-center bg-bg p-4">
      <Card variant="float" className="w-full max-w-md">
        <CardHeader className="items-center text-center">
          <Mark className="mb-2" />
          {mfaChallenge ? (
            <>
              <div className="grid h-10 w-10 place-items-center rounded-2 bg-mint-soft text-mint-deep mb-1">
                <Shield className="h-5 w-5" strokeWidth={1.8} />
              </div>
              <CardTitle className="font-display text-2xl">Verificação em 2 fatores</CardTitle>
              <CardDescription>Digite o código de 6 dígitos do seu app autenticador</CardDescription>
            </>
          ) : (
            <>
              <CardTitle className="font-display text-2xl">Entre na sua conta</CardTitle>
              <CardDescription>Gestão e automação de prestação de contas</CardDescription>
            </>
          )}
        </CardHeader>
        <CardContent>
          {mfaChallenge ? (
            <form onSubmit={onSubmitMfa} className="space-y-4">
              <div>
                <Label htmlFor="mfa-code" className="sr-only">
                  Código
                </Label>
                <Input
                  id="mfa-code"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  placeholder="000000"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center font-mono text-[24px] tracking-[0.4em] h-14"
                  autoFocus
                  autoComplete="one-time-code"
                />
              </div>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full"
                disabled={mfaSubmitting || mfaCode.length !== 6}
              >
                {mfaSubmitting ? 'Verificando…' : 'Entrar'}
              </Button>
              <button
                type="button"
                onClick={backToPassword}
                className="w-full flex items-center justify-center gap-1.5 text-xs text-fg-muted hover:text-fg py-2"
              >
                <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.8} />
                Voltar
              </button>
            </form>
          ) : (
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="voce@suaentidade.org.br"
                  {...form.register('email')}
                />
                {form.formState.errors.email && (
                  <p className="text-xs text-block">{form.formState.errors.email.message}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Senha</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  {...form.register('password')}
                />
                {form.formState.errors.password && (
                  <p className="text-xs text-block">{form.formState.errors.password.message}</p>
                )}
              </div>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full"
                disabled={form.formState.isSubmitting}
              >
                {form.formState.isSubmitting ? 'Entrando…' : 'Entrar'}
              </Button>
            </form>
          )}
          {!mfaChallenge && (
            <p className="text-center text-xs text-fg-muted mt-6">
              Esqueceu a senha?{' '}
              <a href="/recuperar" className="text-mint-deep hover:underline">
                Recupere aqui
              </a>
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
